import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Alert,
  CircularProgress,
  LinearProgress,
  Stack,
  Snackbar
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import CampaignIcon from '@mui/icons-material/Campaign';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { api } from '../../services/auth';

const SubscriptionSettings = () => {
  const { subscription, modules } = useSubscription();
  const [quotaData, setQuotaData] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [quotaError, setQuotaError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadQuotas = async () => {
      try {
        setQuotaLoading(true);
        const response = await api.get('/quotas/me', { withCredentials: true });
        if (!isMounted) return;
        setQuotaData(response.data);
        setQuotaError(null);
      } catch (err) {
        if (!isMounted) return;
        console.error('[SubscriptionSettings] Failed to load quota data', err);
        setQuotaError("Impossible de charger les quotas pour le moment.");
      } finally {
        if (isMounted) {
          setQuotaLoading(false);
        }
      }
    };

    loadQuotas();
    return () => {
      isMounted = false;
    };
  }, []);

  const quotaScope = quotaData?.plan?.quota_scope || 'user';
  const usageSource = quotaScope === 'brand' ? quotaData?.brand_usage : quotaData?.usage;
  const chatUsageSource = quotaScope === 'brand' ? quotaData?.brand_daily_usage : quotaData?.daily_usage;
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return value.toLocaleString('fr-FR');
  };

  const computeProgress = (current, limit) => {
    if (!limit || limit <= 0) return 0;
    return Math.min(100, (current / limit) * 100);
  };

  const quotaMetrics = quotaData
    ? [
        {
          key: 'contacts',
          title: 'Contacts mensuels',
          current: usageSource?.contacts_created ?? 0,
          limit: quotaData?.plan?.max_contacts,
          helper: 'Créations de contacts ce mois-ci',
        },
        {
          key: 'emails',
          title: 'Emails mensuels',
          current: usageSource?.emails_sent ?? 0,
          limit: quotaData?.plan?.emails_included_monthly,
          helper: 'Envois email',
        },
        {
          key: 'sms',
          title: 'SMS mensuels',
          current: usageSource?.sms_sent ?? 0,
          limit: quotaData?.plan?.sms_included_monthly,
          helper: 'SMS envoyés ce mois-ci',
        },
        {
          key: 'chat',
          title: 'Requêtes chat (jour)',
          current: chatUsageSource?.chat_requests_sent ?? 0,
          limit: quotaData?.plan?.chat_requests_daily,
          helper: "Limite quotidienne d'assistants IA",
        },
      ]
    : [];

  const brandDisplayName = quotaData?.brand?.label || quotaData?.brand?.name;
  const effectivePlanName =
    quotaData?.plan?.subscription_name ||
    quotaData?.brand?.subscription_name ||
    subscription ||
    'Demo';

  // Plan configurations
  const planConfig = {
    Demo: {
      color: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
      price: 'Gratuit (14 jours)',
      description: 'Idéal pour découvrir la plateforme'
    },
    Growth: {
      color: 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)',
      price: '49 000 FCFA/mois',
      description: 'Parfait pour les PME en pleine croissance'
    },
    Pro: {
      color: 'linear-gradient(135deg, #ab47bc 0%, #8e24aa 100%)',
      price: '99 000 FCFA/mois',
      description: 'Pour les entreprises avec des besoins avancés'
    },
    Enterprise: {
      color: 'linear-gradient(135deg, #ffa726 0%, #f57c00 100%)',
      price: 'Sur devis',
      description: 'Solutions personnalisées pour grandes entreprises'
    }
  };

  // Module categories with icons
  const categoryIcons = {
    CONTACTS_VOLUMES: <StorageIcon />,
    CDP_CRM: <PeopleIcon />,
    MARKETING_AUTO: <CampaignIcon />,
    IA_ANALYTICS: <PsychologyIcon />,
    FIDELITE: <LoyaltyIcon />,
    SUPPORT: <SupportAgentIcon />
  };

  const categoryNames = {
    CONTACTS_VOLUMES: 'Contacts & Volumes',
    CDP_CRM: 'CDP & CRM',
    MARKETING_AUTO: 'Marketing Automation',
    IA_ANALYTICS: 'IA & Analytics',
    FIDELITE: 'Fidélité & Engagement',
    SUPPORT: 'Support & Services'
  };

  // Module details with categories
  const moduleDetails = {
    // CONTACTS & VOLUMES
    max_contacts: { name: 'Nombre maximum de contacts', category: 'CONTACTS_VOLUMES' },
    email_volume: { name: "Volume d'emails inclus / mois", category: 'CONTACTS_VOLUMES' },
    sms_volume: { name: 'Volume SMS inclus', category: 'CONTACTS_VOLUMES' },
    additional_emails: { name: 'Email additionnel', category: 'CONTACTS_VOLUMES' },
    
    // CDP & CRM
    view_360: { name: 'Vue 360° client', category: 'CDP_CRM' },
    manual_import: { name: 'Import manuel de contacts', category: 'CDP_CRM' },
    auto_reconciliation: { name: 'Réconciliation automatique des profils', category: 'CDP_CRM' },
    visitor_tracking: { name: 'Tracking visiteurs / clients', category: 'CDP_CRM' },
    landing_page_integration: { name: 'Intégration auto depuis landing pages', category: 'CDP_CRM' },
    custom_api_integrations: { name: 'Intégrations & API personnalisées', category: 'CDP_CRM' },
    
    // MARKETING AUTOMATION
    ai_campaign_creation: { name: 'Création de campagnes IA', category: 'MARKETING_AUTO' },
    manual_campaign_assistant: { name: 'Assistant de campagne manuel', category: 'MARKETING_AUTO' },
    email_editor: { name: "Édition d'emails (IA & manuelle)", category: 'MARKETING_AUTO' },
    ab_testing: { name: 'A/B Testing', category: 'MARKETING_AUTO' },
    preview_spam_test: { name: 'Prévisualisation & test anti-spam', category: 'MARKETING_AUTO' },
    sms_campaigns: { name: 'Campagnes SMS (add-on)', category: 'MARKETING_AUTO' },
    remove_branding: { name: 'Suppression logo Qilinsa Email (add-on)', category: 'MARKETING_AUTO' },
    social_integration: { name: 'Intégration sociale (Facebook, Insta, WhatsApp)', category: 'MARKETING_AUTO' },
    
    // IA & ANALYTICS
    ai_campaign_analysis: { name: 'Analyse IA des campagnes & suggestions', category: 'IA_ANALYTICS' },
    ai_segment_analysis: { name: 'Analyse IA avancée des segments', category: 'IA_ANALYTICS' },
    basic_ai_chat: { name: 'Chat IA basique', category: 'IA_ANALYTICS' },
    basic_dashboards: { name: 'Tableaux de bord & KPIs essentiels', category: 'IA_ANALYTICS' },
    advanced_dashboards: { name: 'Tableaux de bord avancés avec IA', category: 'IA_ANALYTICS' },
    
    // FIDÉLITÉ
    loyalty_program: { name: 'Gestion du programme de fidélité', category: 'FIDELITE' },
    multi_brand: { name: 'Multi-brand / multi-tenant', category: 'FIDELITE' },
    
    // SUPPORT
    support_standard: { name: 'Support Standard', category: 'SUPPORT' },
    support_priority: { name: 'Support Prioritaire', category: 'SUPPORT' },
    support_dedicated: { name: 'Support Dédié (SLA)', category: 'SUPPORT' },
    account_manager: { name: 'Gestionnaire de compte dédié', category: 'SUPPORT' }
  };

  // Group modules by category
  const modulesByCategory = {};
  modules.forEach(moduleCode => {
    const moduleInfo = moduleDetails[moduleCode];
    if (moduleInfo) {
      if (!modulesByCategory[moduleInfo.category]) {
        modulesByCategory[moduleInfo.category] = [];
      }
      modulesByCategory[moduleInfo.category].push({
        code: moduleCode,
        name: moduleInfo.name
      });
    }
  });

  const currentPlan = planConfig[effectivePlanName] || planConfig.Demo;

  return (
    <>
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontFamily: 'Montserrat, Arial, sans-serif' }}>
          Mon Abonnement
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gérez votre plan et consultez vos fonctionnalités disponibles
        </Typography>
      </Box>

      {/* Current Plan Card */}
      <Card 
        sx={{ 
          mb: 4,
          background: currentPlan.color,
          color: 'white',
          position: 'relative',
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.9, fontWeight: 600 }}>
                Plan Actuel
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {effectivePlanName}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                {currentPlan.price}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {currentPlan.description}
              </Typography>
            </Box>
            <Chip 
              label={`${modules.length} Fonctionnalités`}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 600,
                fontSize: 14
              }}
            />
          </Box>
          
          <Button
            variant="contained"
            startIcon={<ContactSupportIcon />}
            disabled
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)'
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.5)'
              }
            }}
          >
            Contacter les ventes
          </Button>
        </CardContent>
      </Card>

      {/* Quota tracking */}
      <Box sx={{ mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} mb={2}>
          <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Montserrat, Arial, sans-serif' }}>
            Suivi des quotas
          </Typography>
          <Chip
            label={quotaScope === 'brand' ? 'Quota partagé par marque' : 'Quota individuel'}
            color={quotaScope === 'brand' ? 'secondary' : 'primary'}
            sx={{ fontWeight: 600 }}
          />
          {quotaScope === 'brand' && brandDisplayName && (
            <Chip
              variant="outlined"
              label={`Marque active : ${brandDisplayName}`}
              sx={{ fontWeight: 500 }}
            />
          )}
        </Stack>

        {quotaLoading ? (
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 140 }}>
              <CircularProgress size={32} />
            </CardContent>
          </Card>
        ) : quotaError ? (
          <Alert severity="error">{quotaError}</Alert>
        ) : quotaData ? (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {quotaMetrics.map((metric) => (
                <Grid item xs={12} md={6} lg={3} key={metric.key}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {metric.title}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, my: 1 }}>
                        {formatNumber(metric.current)}{' '}
                        {metric.limit != null && metric.limit !== undefined
                          ? `/ ${formatNumber(metric.limit)}`
                          : ''}
                      </Typography>
                      {metric.limit != null && metric.limit !== undefined ? (
                        <>
                          <LinearProgress
                            variant="determinate"
                            value={computeProgress(metric.current, metric.limit)}
                            sx={{ height: 8, borderRadius: 4, mb: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {metric.helper}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Illimité sur ce plan
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Webhook section temporarily hidden */}
          </>
        ) : (
          <Alert severity="info">Aucune donnée de quota disponible.</Alert>
        )}
      </Box>

      {/* Features by Category */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, fontFamily: 'Montserrat, Arial, sans-serif' }}>
        Fonctionnalités Incluses
      </Typography>

      <Grid container spacing={3}>
        {Object.entries(modulesByCategory).map(([category, categoryModules]) => (
          <Grid item xs={12} md={6} key={category}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: 'primary.main', mr: 1.5 }}>
                    {categoryIcons[category]}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {categoryNames[category]}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {categoryModules.map((module, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={module.name}
                        primaryTypographyProps={{
                          fontSize: 14
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Empty state if no modules */}
      {modules.length === 0 && (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Aucun module disponible
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Contactez-nous pour activer votre abonnement
          </Typography>
        </Card>
      )}
    </Container>
    </>
  );
};

export default SubscriptionSettings;

